import { EnvelopeIcon } from '@heroicons/react/24/outline'
import { ArrowLeftIcon } from '@heroicons/react/24/solid'
import { NavLink } from 'react-router-dom'
import { Button } from 'app/components/ui/button'
import { FormInput } from 'app/components/ui/form-input'
import { FormPassword } from 'app/components/ui/form-password'
import Spinner from 'app/components/Spinner'
import { buildGoogleOAuthUrl } from './googleOAuth'
import { LoginActionData } from './useLogin'

export type LoginShadcnComponentProps = {
  email: string
  setEmail: (email: string) => void
  password: string
  setPassword: (password: string) => void
  loading: boolean
  isSubmitting: boolean
  redirectTo: string
  actionData: LoginActionData | undefined
  codeFormRef: React.RefObject<HTMLFormElement | null>
  channelRef: React.RefObject<HTMLInputElement | null>
  otpValue: string
  setOtpValue: (value: string) => void
  resend: () => void
  backToLogin: () => void
  handleLogin: () => Promise<void>
  handleVerifyOtp: (code?: string) => Promise<void>
}

function TwoFactorForm({
  actionData,
  codeFormRef,
  channelRef,
  loading,
  otpValue,
  setOtpValue,
  resend,
  backToLogin,
  handleVerifyOtp,
}: {
  actionData: LoginActionData
  codeFormRef: React.RefObject<HTMLFormElement | null>
  channelRef: React.RefObject<HTMLInputElement | null>
  loading: boolean
  otpValue: string
  setOtpValue: (value: string) => void
  resend: () => void
  backToLogin: () => void
  handleVerifyOtp: (code?: string) => Promise<void>
}) {
  function checkSubmit() {
    const ready = [1, 2, 3, 4, 5, 6].every((i) => {
      const d = (document.querySelector(`[name=code${i}]`) as HTMLInputElement)?.value
      return d?.match(/^[0-9]$/)
    })
    if (ready) {
      const code = [1, 2, 3, 4, 5, 6]
        .map((i) => (document.querySelector(`[name=code${i}]`) as HTMLInputElement)?.value)
        .join('')
      setOtpValue(code)
      handleVerifyOtp(code)
    }
  }

  function handleResend() {
    ;[1, 2, 3, 4, 5, 6].forEach((i) => {
      ;(document.querySelector(`[name=code${i}]`) as HTMLInputElement).value = ''
    })
    resend()
  }

  return (
    <form
      ref={codeFormRef}
      className="w-full"
      onSubmit={(e) => {
        e.preventDefault()
        handleVerifyOtp()
      }}
    >
      <h2 className="mb-5">Check your {actionData.channel?.toUpperCase() === 'EMAIL' ? 'Email' : 'texts'}</h2>
      <p className="mb-8 text-center text-subtle">We sent you a code</p>
      <input type="hidden" name="email" value={actionData.email || ''} />
      <input type="hidden" name="password" value={actionData.password || ''} />
      <input type="hidden" name="code" value={otpValue} />
      <div className="flex justify-center">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <input
            autoFocus={i === 1}
            className="m-1 my-8 w-10 rounded-lg border border-default bg-surface px-0 py-2 text-center text-2xl text-strong shadow-elevation-1 focus:border-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-alpha-8 sm:m-2 sm:w-16 sm:px-2 sm:text-4xl"
            key={`code${i}`}
            type="tel"
            name={`code${i}`}
            maxLength={1}
            tabIndex={i}
            autoComplete="off"
            placeholder=""
            onPaste={(event) => {
              event.preventDefault()
              const code = event.clipboardData.getData('text')
              if (!code.match(/^[0-9]+$/)) return
              const digits = code.split('')
              digits.forEach((d, idx) => {
                const el = document.querySelector(`[name=code${idx + 1}]`) as HTMLInputElement
                if (el) el.value = d || ''
              })
              checkSubmit()
            }}
            onKeyDown={(event) => {
              if (event.metaKey || event.ctrlKey || event.altKey || ['Tab', 'Backspace', 'Delete'].includes(event.key))
                return
              if (!event.key.match(/[0-9]/)) return event.preventDefault()
            }}
            onKeyUp={(event) => {
              if (!event.key.match(/[0-9]/)) return
              ;(document.querySelector(`[name=code${i + 1}]`) as HTMLElement)?.focus()
              checkSubmit()
            }}
          />
        ))}
      </div>
      {actionData.error && (
        <p className="text-sm text-red-600" role="alert">
          {actionData.error}
        </p>
      )}
      <input type="hidden" name="channel" value={actionData.channel || ''} ref={channelRef} />
      <Button type="submit" variant="primary" size="lg" loading={loading} className="my-8 w-full">
        Submit Code
      </Button>
      <div className="my-5 text-center text-default">
        Didn't receive it?{' '}
        <Button type="button" variant="link" size="inline" onClick={handleResend}>
          Click to resend
        </Button>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="inline"
        onClick={backToLogin}
        className="mx-auto flex gap-2 font-semibold hover:text-strong"
      >
        <ArrowLeftIcon className="h-4 w-4" /> Back to login
      </Button>
    </form>
  )
}

function LoginForm({
  email,
  setEmail,
  password,
  setPassword,
  loading,
  isSubmitting,
  actionData,
  handleLogin,
}: {
  email: string
  setEmail: (email: string) => void
  password: string
  setPassword: (password: string) => void
  loading: boolean
  isSubmitting: boolean
  actionData: LoginActionData | undefined
  handleLogin: () => Promise<void>
}) {
  return (
    <div className="w-full">
      <h2 className="mb-5">Log in to your account</h2>
      <p className="mb-12 text-subtle">Welcome back! Please enter your details.</p>

      <Button
        variant="tertiary"
        size="lg"
        className="w-full"
        type="button"
        onClick={() => {
          window.location.href = buildGoogleOAuthUrl(window.location.origin, __GOOGLE_CLIENT_ID__)
        }}
      >
        <img
          alt="Google Logo"
          src="https://lh3.googleusercontent.com/COxitqgJr1sJnIDe8-jiKhxDx1FrYbtRHKJ9z_hELisAlapwE9LUPh6fcXIfb5vwpbMl4xl9H9TRFPc5NOO8Sb3VSgIBrfRYvW6cUA"
          className="mr-1 inline h-5 w-5"
        />
        Log in with Google
      </Button>

      <div className="separator my-5 text-strong">OR</div>

      <form
        id="login-form"
        className="w-full"
        onSubmit={(e) => {
          e.preventDefault()
          handleLogin()
        }}
      >
        <div className="mt-6 grid w-full grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-6">
          <FormInput
            icon={<EnvelopeIcon className="h-5 w-5 text-strong" />}
            cols={6}
            label="Email Address"
            labelClassName="text-body-sm font-normal"
            wrapperClassName="!gap-1"
            id="email"
            required
            autoFocus={true}
            name="email"
            type="email"
            autoComplete="email"
            aria-describedby="email-error"
            value={email}
            onChange={(event) => setEmail(event.currentTarget.value)}
          />

          <FormPassword
            label="Password"
            labelClassName="text-body-sm font-normal"
            wrapperClassName="!gap-1"
            name="password"
            cols={6}
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
          />

          {actionData?.error && (
            <p className="col-span-6 text-center text-sm font-medium text-red-600" role="alert">
              {actionData.error}
            </p>
          )}
        </div>

        <div className="mt-8 flex flex-col items-center gap-4">
          <Button id="login" variant="primary" size="lg" type="submit" className="w-full">
            {isSubmitting || loading ? <Spinner /> : 'Log in'}
          </Button>

          <NavLink
            to={`/forgot?email=${encodeURIComponent(email)}`}
            className="block text-center text-subtle hover:text-strong"
          >
            Forgot your password?
          </NavLink>
        </div>
      </form>
    </div>
  )
}

export function LoginShadcnComponent({
  email,
  setEmail,
  password,
  setPassword,
  loading,
  isSubmitting,
  actionData,
  codeFormRef,
  channelRef,
  otpValue,
  setOtpValue,
  resend,
  backToLogin,
  handleLogin,
  handleVerifyOtp,
}: LoginShadcnComponentProps) {
  return (
    <div className="grid h-dvh place-items-center overflow-y-auto bg-surface-subtle p-4">
      <div className="w-full max-w-2xl rounded border border-default bg-surface p-6 text-center shadow sm:p-16">
        {actionData?.getCode ? (
          <TwoFactorForm
            actionData={actionData}
            codeFormRef={codeFormRef}
            channelRef={channelRef}
            loading={loading}
            otpValue={otpValue}
            setOtpValue={setOtpValue}
            resend={resend}
            backToLogin={backToLogin}
            handleVerifyOtp={handleVerifyOtp}
          />
        ) : (
          <LoginForm
            email={email}
            setEmail={setEmail}
            password={password}
            setPassword={setPassword}
            loading={loading}
            isSubmitting={isSubmitting}
            actionData={actionData}
            handleLogin={handleLogin}
          />
        )}
      </div>
    </div>
  )
}
